import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Locale BCP-47 używane do formatowania dat, wybierane na podstawie języka aplikacji.
 *
 * Dla EN wybrano `en-GB`, a nie `en-US`, żeby zachować kolejność dzień-miesiąc
 * spójną z wariantem polskim (22/07/2026, nie 07/22/2026).
 */
const DATE_LOCALE_BY_LANGUAGE: Record<string, string> = {
  pl: 'pl-PL',
  en: 'en-GB',
};

const FALLBACK_DATE_LOCALE = 'pl-PL';

/** Mapuje kod języka i18next ('pl', 'en-US', undefined) na locale do formatowania dat. */
export function resolveDateLocale(language: string | undefined | null): string {
  const base = (language ?? '').split('-')[0]?.toLowerCase() ?? '';
  return DATE_LOCALE_BY_LANGUAGE[base] ?? FALLBACK_DATE_LOCALE;
}

/**
 * Znacznik czasu z sekundami — dla widoków diagnostycznych (log audytu, historia
 * zmian, backupy), gdzie kolejność zdarzeń w obrębie minuty bywa istotna.
 */
export const DATE_TIME_WITH_SECONDS: Intl.DateTimeFormatOptions = {
  dateStyle: 'short',
  timeStyle: 'medium',
};

export interface DateFormatters {
  /** Locale aktualnie użyte do formatowania — przydatne w zależnościach useMemo. */
  locale: string;
  /** Sama data. */
  formatDate: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
  /** Data z godziną. */
  formatDateTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
  /** Sama godzina. */
  formatTime: (value: string | number | Date, options?: Intl.DateTimeFormatOptions) => string;
}

/** Zastępuje nieparsowalną datę myślnikiem zamiast wypisywać "Invalid Date". */
const INVALID_DATE_PLACEHOLDER = '—';

function toDate(value: string | number | Date): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Formatowanie dat zgodne z aktualnym językiem aplikacji.
 *
 * Używać zamiast gołego `toLocaleString()` (bierze locale przeglądarki, więc przy
 * aplikacji przełączonej na EN i systemie po polsku data zostaje polska) oraz zamiast
 * `toLocaleString('pl-PL')` (locale zabetonowane, nigdy się nie przełączy).
 */
export function useDateFormat(): DateFormatters {
  const { i18n } = useTranslation();
  const locale = resolveDateLocale(i18n.language);

  // Tożsamość zwracanych funkcji zmienia się WYŁĄCZNIE wraz z locale. Dzięki temu
  // `useMemo`/`useEffect` w komponentach mogą trzymać sam formatter w zależnościach
  // i przeliczać się dokładnie przy zmianie języka, a nie przy każdym renderze.
  return useMemo<DateFormatters>(() => {
    const format = (
      value: string | number | Date,
      options: Intl.DateTimeFormatOptions,
    ): string => {
      const d = toDate(value);
      return d ? d.toLocaleString(locale, options) : INVALID_DATE_PLACEHOLDER;
    };

    return {
      locale,
      formatDate: (value, options) =>
        format(value, options ?? { dateStyle: 'short' }),
      formatDateTime: (value, options) =>
        format(value, options ?? { dateStyle: 'short', timeStyle: 'short' }),
      formatTime: (value, options) =>
        format(value, options ?? { timeStyle: 'short' }),
    };
  }, [locale]);
}
